import * as React from 'react';
import { ErrorMessageWrapper } from '../internal/ErrorMessageWrapper';
import { st, classes } from './Dropdown.st.css';
import { useTPAComponentsContext } from '../TPAComponentsConfig';
import { DATA_HOOKS } from './constants';
import { CoreDropdown } from '../internal/WixUiCoreComponents/components/CoreDropdown';
import classNames from 'classnames';
import { DropdownBase } from './DropdownBase';
import { modifyOptions } from './helpers';
import { OptionFactory, } from '../internal/WixUiCoreComponents/components/CoreDropdownOption';
import { DropdownOption } from './DropdownOption';
import { DropdownNativeSelect } from './DropdownNativeSelect';
import { DropdownTheme, } from './types';
import { getAriaDescribedBy } from '../../common/a11y';
import { IconTooltip } from '../IconTooltip';
import { Error } from '@wix/wix-ui-icons-common/on-stage';
import { TooltipSkin } from '../Tooltip';
import uniqueId from '../../common/uniqueId';
import { deprecationLog, unwrap, wrap } from '../../common/deprecationLog';
import { Asterisk } from '../internal/Asterisk/Asterisk';
import { useComboboxPatternKeys } from './useComboboxPatternKeys';
export const Dropdown = React.forwardRef((props, ref) => {
    const { label, alignment = 'bottom', className, theme = DropdownTheme.Box, newErrorMessage, error, lang, errorAppearance, errorMessage, outerBorder = false, options = [], initialSelectedId, selectedId, readOnly, upgrade, dropdownId, optionsContainerId, mobileNativeSelect, 'aria-required': ariaRequired, showAsterisk, } = props;
    const rootRef = React.useRef();
    const dropdownRef = React.useRef();
    const nativeDropdownRef = React.useRef();
    const { mobile, rtl } = useTPAComponentsContext();
    const [focused, setFocused] = React.useState(false);
    const [isErrorTooltipShown, setIsErrorTooltipShown] = React.useState(false);
    const [isOpen, setIsOpen] = React.useState(false);
    const [selectedOption, setSelectedOption] = React.useState(selectedId
        ? options.find((o) => o.id === selectedId) || null
        : options.find((o) => o.id === initialSelectedId) || null);
    const errorMessageId = React.useMemo(() => uniqueId('error-message'), []);
    const labelForId = React.useMemo(() => uniqueId('label-for-id_'), []);
    const contentId = React.useMemo(() => optionsContainerId ?? uniqueId('dropdown-options-container_'), [optionsContainerId]);
    React.useEffect(() => {
        wrap('Button');
    }, []);
    React.useEffect(() => {
        if (!upgrade) {
            deprecationLog('Dropdown', 'The current `Dropdown` component API will be deprecated in a future version. Please use the `upgrade` prop in order to use the new API.');
            unwrap('Button');
        }
    }, [upgrade]);
    React.useEffect(() => {
        if (selectedId) {
            setSelectedOption(options.find((o) => o.id === selectedId) || null);
            return;
        }
        if (initialSelectedId) {
            const updatedOption = options.find((o) => o.id === initialSelectedId);
            if (updatedOption) {
                setSelectedOption(updatedOption);
            }
        }
    }, [selectedId, options]);
    React.useImperativeHandle(ref, () => ({
        focus() {
            if (dropdownRef?.current && !mobileNativeSelect) {
                dropdownRef.current.focus();
            }
            if (nativeDropdownRef?.current && mobileNativeSelect) {
                nativeDropdownRef.current.focus();
            }
        },
    }), []);
    const onSelect = (selectedOption) => {
        if (!selectedOption) {
            return;
        }
        const { onChange } = props;
        if (!selectedId) {
            setSelectedOption(selectedOption);
        }
        setIsOpen(false);
        onChange && onChange(options.find(({ id }) => selectedOption.id === id));
    };
    const { descendant, handleKeydown, matchedOptions, matchIndex, selectFocused, } = useComboboxPatternKeys(options, contentId, onSelect, isOpen, setIsOpen, rootRef);
    const onExpandedChangeWrapper = (isOpen) => {
        const { onExpandedChange } = props;
        setIsOpen(isOpen);
        if (typeof onExpandedChange === 'function') {
            onExpandedChange(isOpen);
        }
    };
    const onCoreSelect = (selectedCoreOption) => {
        if (!selectedCoreOption) {
            return;
        }
        const selectedOption = options.find(({ id }) => selectedCoreOption.id === id);
        onSelect(selectedOption);
    };
    const toggleTooltip = (showTooltip) => {
        setIsErrorTooltipShown(showTooltip);
    };
    const onErrorButtonClick = (e) => {
        toggleTooltip(!isErrorTooltipShown);
        e.stopPropagation();
    };
    const onKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
        }
    };
    const onFocus = () => {
        if (!focused) {
            toggleTooltip(true);
        }
        setFocused(true);
    };
    const onBlur = (e) => {
        if (rootRef.current &&
            // TODO: relatedTarget is an experimental feature, so remove this comment if it's no longer experimental or we've found a non-experimental solution
            !rootRef.current.contains(e.relatedTarget)) {
            setFocused(false);
            setIsErrorTooltipShown(false);
        }
        if (isOpen) {
            selectFocused();
        }
    };
    const onErrorIconMouseDown = () => {
        setFocused(true);
    };
    const getErrorIconTooltip = () => {
        const { errorTooltipPlacement } = props;
        return (React.createElement(IconTooltip, { "data-hook": DATA_HOOKS.errorTooltip, className: classes.dropdownError, message: errorMessage, icon: React.createElement(Error, { className: classes.errorIcon }), tooltipId: errorMessageId, tooltipSkin: TooltipSkin.Error, showTooltip: isErrorTooltipShown, onClick: onErrorButtonClick, onMouseDown: onErrorIconMouseDown, onKeyDown: onKeyDown, tooltipProps: {
                placement: errorTooltipPlacement,
            } }));
    };
    const nativeDropdown = () => {
        const { placeholder, disabled, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, name, isLoading = false, 'loading-aria-label': loadingAriaLabel, } = props;
        return (React.createElement(DropdownNativeSelect, { readOnly: readOnly, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, "aria-required": ariaRequired, disabled: disabled, className: classes.dropdownNativeSelect, error: error, name: name, ref: nativeDropdownRef, errorMessage: errorMessage, options: modifyOptions(options), onSelect: (selectedOption) => onSelect(selectedOption), placeholder: placeholder, selectedOption: selectedOption, errorComponent: getErrorIconTooltip(), newErrorMessage: newErrorMessage, isLoading: isLoading, "loading-aria-label": loadingAriaLabel }));
    };
    const coreOptions = modifyOptions(options).map((option) => option.divider
        ? OptionFactory.createDivider({
            className: classes.divider,
        })
        : OptionFactory.create({
            ...option,
            render: () => (React.createElement(DropdownOption, { unavailable: option.unavailable, className: classes.dropdownOption, ...option, descendantId: `${contentId}_option-${option.id}`, isFocused: matchedOptions[matchIndex]?.id === option.id })),
        }));
    const coreDropdown = () => {
        const { placeholder = '', disabled, forceContentElementVisibility, placement = 'bottom', 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, flip, fixed, appendTo, optionsContainerZIndex, optionsContainerClass, 'aria-describedby': ariaDescribedByProp, dynamicWidth, isLoading = false, 'loading-aria-label': loadingAriaLabel, } = props;
        const appendedToParent = !appendTo || appendTo === 'parent';
        const ariaDescribedBy = getAriaDescribedBy([
            ariaDescribedByProp,
            errorMessage ? errorMessageId : '',
        ]);
        return (React.createElement(CoreDropdown, { optionsContainerZIndex: optionsContainerZIndex, className: st(classes.root, { rtl, alignment, isMobile: mobile, theme, readOnly }, classNames(classes.dropdown, optionsContainerClass, {
                /*
                when dropdown is appended to a component other than parent,
                we need to pass consumer's className to the core Dropdown in order for it to have affect on the portal
              */
                [className]: !appendedToParent,
            })), contentClassName: classes.dropdownContainer, placement: placement, "data-hook": DATA_HOOKS.coreDropdown, "data-mobile": mobile, options: coreOptions, readOnly: readOnly, onDeselect: onCoreSelect, onSelect: onCoreSelect, initialSelectedIds: selectedOption ? [selectedOption.id] : [], onInitialSelectedOptionsSet: () => { }, forceContentElementVisibility: forceContentElementVisibility, contentId: contentId, openTrigger: disabled || readOnly ? undefined : 'click', onExpandedChange: onExpandedChangeWrapper, flip: flip, fixed: fixed, appendTo: appendTo, dynamicWidth: dynamicWidth || !appendedToParent, dropdownA11yFixes: true, preventContentKeydown: true },
            React.createElement(DropdownBase, { className: classes.dropdownBase, selectedOption: selectedOption, placeholder: placeholder, disabled: disabled, readOnly: readOnly, error: error, upgrade: upgrade, ref: dropdownRef, rtl: rtl, id: dropdownId ?? labelForId, "aria-activedescendant": isOpen ? descendant : undefined, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, "aria-required": ariaRequired, isExpanded: isOpen, "aria-describedby": ariaDescribedBy, isLoading: isLoading, "loading-aria-label": loadingAriaLabel, "aria-controls": contentId }),
            error && errorMessage && !newErrorMessage && getErrorIconTooltip()));
    };
    const Wrapper = newErrorMessage ? ErrorMessageWrapper : React.Fragment;
    const renderRequiredAsterisk = ariaRequired && showAsterisk;
    const stStates = {
        alignment,
        mobile,
        theme,
        rtl,
        newErrorMessage,
        error,
        outerBorder,
        readOnly,
    };
    return (React.createElement(Wrapper, { ...(newErrorMessage && {
            visible: error && !!errorMessage,
            message: errorMessage,
            errorAppearance,
            messageId: errorMessageId,
            className: st(classes.root, stStates, className),
            'data-mobile': mobile,
            lang,
            'data-hook': props['data-hook'],
        }) },
        React.createElement("div", { ...(!newErrorMessage && {
                className: st(classes.root, stStates, className),
                'data-mobile': mobile,
                'data-hook': props['data-hook'],
                lang,
            }), ref: rootRef, onFocus: onFocus, onBlur: onBlur, onKeyDown: handleKeydown },
            label && (React.createElement("label", { "data-hook": DATA_HOOKS.label, htmlFor: labelForId, className: classes.label },
                label,
                renderRequiredAsterisk && (React.createElement(Asterisk, { dataHook: DATA_HOOKS.labelAsterisk })))),
            mobileNativeSelect && mobile ? nativeDropdown() : coreDropdown())));
});
//# sourceMappingURL=Dropdown.js.map